import {Save, BookOpen, Printer, User, LogOut, Menu, Copy} from 'lucide-react'
import { useNavigate } from "react-router-dom"

interface DashboardHeaderProps {
  isAuthenticated: boolean
  user: { userName?: string; email?: string } | null
  loadedRecipeId: string | null
  loadedRecipeName: string
  showMobileMenu: boolean
  setShowMobileMenu: (v: boolean) => void
  onSaveClick: () => void
  onSaveAsNewClick: () => void
  onSignOut: () => void
  onShowAuth: () => void
  onShowPrintable: () => void
}

export function DashboardHeader({
  isAuthenticated,
  user,
  loadedRecipeId,
  loadedRecipeName,
  showMobileMenu,
  setShowMobileMenu,
  onSaveClick,
  onSaveAsNewClick,
  onSignOut,
  onShowAuth,
  onShowPrintable,
}: DashboardHeaderProps) {
  const navigate = useNavigate()

  const handleRecipesClick = () => {
    if (isAuthenticated) {
      navigate("/recipes")
    } else {
      onShowAuth()
    }
  }

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
      <div className="max-w-[1800px] mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900">
              Calculator Soap
            </h1>
            <p className="text-xs md:text-sm text-gray-600 hidden sm:block">
              Professional Soap Recipe Dashboard
            </p>
          </div>
          <div className="flex items-center gap-2">
            {loadedRecipeName && (
              <span className="hidden md:inline text-sm text-emerald-600 font-medium bg-emerald-50 px-3 py-1 rounded-lg">
                {loadedRecipeName}
              </span>
            )}
            <button
              onClick={onSaveClick}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors shadow-md"
              title={loadedRecipeId ? "Update recipe" : "Save recipe"}
            >
              <Save size={18} />
              <span className="hidden sm:inline">{loadedRecipeId ? "Update" : "Save"}</span>
            </button>
            {loadedRecipeId ? (
              <button
                onClick={onSaveAsNewClick}
                className="flex items-center gap-2 px-4 py-2 bg-white text-emerald-700 border border-emerald-200 rounded-lg hover:bg-emerald-50 transition-colors"
                title="Save as a new copy without changing the original"
              >
                <Copy size={18} />
                <span className="hidden sm:inline">Save as new</span>
              </button>
            ) : null}
            <button
              onClick={handleRecipesClick}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              title="My saved recipes"
            >
              <BookOpen size={18} />
              <span className="hidden sm:inline">My Recipes</span>
            </button>
            <button
              onClick={onShowPrintable}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <Printer size={18} />
              <span className="hidden sm:inline">Print</span>
            </button>
            {isAuthenticated ? (
              <div className="hidden sm:flex items-center gap-2 ml-1">
                <span className="text-sm text-gray-600 max-w-[120px] truncate">
                  {user?.userName || user?.email}
                </span>
                <button
                  onClick={onSignOut}
                  className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  title="Sign out"
                >
                  <LogOut size={18} />
                </button>
              </div>
            ) : (
              <button
                onClick={onShowAuth}
                className="hidden sm:flex items-center gap-2 px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <User size={18} />
                <span className="text-sm">Sign In</span>
              </button>
            )}
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="lg:hidden p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              <Menu size={24} />
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {showMobileMenu && (
          <nav className="lg:hidden mt-4 pt-4 border-t border-gray-200">
            <div className="flex flex-col gap-2">
              {loadedRecipeId ? (
                <button onClick={onSaveAsNewClick} className="text-left text-gray-700 hover:text-emerald-600 py-2">Save as new</button>
              ) : null}
              <button onClick={handleRecipesClick} className="text-left text-gray-700 hover:text-emerald-600 py-2">My Saved Recipes</button>
              <a href="#safety" className="text-gray-700 hover:text-emerald-600 py-2">Lye Safety Guide</a>
              <a href="#beginners" className="text-gray-700 hover:text-emerald-600 py-2">Soap Making for Beginners</a>
              <a href="#properties" className="text-gray-700 hover:text-emerald-600 py-2">Oil Properties Chart</a>
              {isAuthenticated && (
                <button onClick={onSignOut} className="text-left text-red-600 hover:text-red-700 py-2">Sign Out</button>
              )}
            </div>
          </nav>
        )}

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex mt-4 pt-4 border-t border-gray-200 gap-6">
          <button onClick={handleRecipesClick} className="text-sm text-gray-700 hover:text-emerald-600 transition-colors">My Saved Recipes</button>
          <a href="#safety" className="text-sm text-gray-700 hover:text-emerald-600 transition-colors">Lye Safety Guide</a>
          <a href="#beginners" className="text-sm text-gray-700 hover:text-emerald-600 transition-colors">Soap Making for Beginners</a>
          <a href="#properties" className="text-sm text-gray-700 hover:text-emerald-600 transition-colors">Oil Properties Chart</a>
        </nav>
      </div>
    </header>
  )
}
